import * as z from "zod";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Controller, type UseFormReturn } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupInput } from "@/components/ui/input-group";
import { ArrowLeft, Loader, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { userFormSchema } from "./schemas";
import type { ApiUser } from "../../types";
import { Switch } from "@/components/ui/switch";
import { useNavigate } from "react-router-dom";
import BottomFixed from "@/components/bottom-fixed";

interface AccountStatusFieldProps {
  form: UseFormReturn<z.infer<typeof userFormSchema>>;
  t: (key: string) => string;
}

const AccountStatusField = ({ form, t }: AccountStatusFieldProps) => (
  <Controller
    name="status"
    control={form.control}
    render={({ field }) => (
      <Field orientation="vertical">
        <FieldContent>
          <FieldLabel htmlFor="account-status">
            {t("keys.account_status")}
          </FieldLabel>
        </FieldContent>
        <Switch
          checked={field.value === "active"}
          onCheckedChange={(checked) =>
            field.onChange(checked ? "active" : "inactive")
          }
          id="account-status"
        />
      </Field>
    )}
  />
);

interface UserFormProps {
  form: UseFormReturn<z.infer<typeof userFormSchema>>;
  onSubmit: (data: z.infer<typeof userFormSchema>) => void;
  onDelete?: (id: string) => void;
  isLoading: boolean;
  user?: ApiUser;
}

const UserForm = ({ form, onSubmit, isLoading, user }: UserFormProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="p-4 rounded-md border w-full flex flex-col gap-y-4 bg-white">
      <form
        id="user-form"
        className="flex gap-5 flex-col md:flex-row"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FieldGroup className="gap-y-5 w-full">
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="email-input">
                  {t("keys.email")} <span className="text-destructive">*</span>
                </FieldLabel>
                <Input
                  {...field}
                  id="email-input"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <Controller
            name="username"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="username-input">
                  {t("keys.username")}
                </FieldLabel>
                <Input
                  {...field}
                  id="username-input"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          {user && (
            <div className="hidden md:block">
              <AccountStatusField form={form} t={t} />
            </div>
          )}
        </FieldGroup>
        <FieldGroup className="gap-y-5">
          <Controller
            name="last_name"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="lastname-input">
                  {t("keys.last_name")}
                </FieldLabel>
                <Input
                  {...field}
                  id="lastname-input"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          <Controller
            name="first_name"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="firstname-input">
                  {t("keys.first_name")}
                </FieldLabel>
                <Input
                  {...field}
                  id="firstname-input"
                  aria-invalid={fieldState.invalid}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />
          {user && (
            <div className="md:hidden">
              <AccountStatusField form={form} t={t} />
            </div>
          )}
        </FieldGroup>
        {!user && (
          <FieldGroup className="gap-y-5">
            <Controller
              name="password"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel htmlFor="password-input">
                    {t("keys.password")}
                  </FieldLabel>
                  <InputGroup>
                    <InputGroupInput
                      {...field}
                      disabled
                      aria-invalid={fieldState.invalid}
                      id="password-input"
                    />
                  </InputGroup>
                  <FieldDescription>
                    {t("modules.users.form.password_change_message")}
                  </FieldDescription>
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          </FieldGroup>
        )}
      </form>
      <BottomFixed className="bg-white border-t border-l">
        <div className="flex flex-wrap flex-row items-center sm:justify-end gap-2">
          {user && (
            <Button
              className="flex-1 sm:flex-none"
              variant="secondary"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft />
              {t("keys.cancel")}
            </Button>
          )}
          <Button
            className="flex-1 sm:flex-none"
            disabled={isLoading}
            type="submit"
            form="user-form"
          >
            {isLoading ? <Loader className="animate-spin" /> : <Save />}
            {t("keys.save")}
          </Button>
        </div>
      </BottomFixed>
    </div>
  );
};

export default UserForm;
